<template>
  <div class="min-h-screen hero-gradient flex items-center justify-center px-6">
    <div class="floating-shapes"></div>
    
    <div class="w-full max-w-md relative z-10">
      <!-- Back to Home -->
      <router-link to="/" class="inline-flex items-center text-gray-300 hover:text-white mb-8 transition-colors">
        <ArrowLeftIcon class="w-4 h-4 mr-2" />
        Back to Home
      </router-link>
      
      <div class="card-glass p-8">
        <div class="text-center mb-8">
          <div class="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <QrCodeIcon class="w-8 h-8 text-white" />
          </div>
          <h1 class="text-3xl font-bold text-gradient mb-2">Get Started</h1>
          <p class="text-gray-300">Create your Digital Menu AI account</p>
        </div>
        
        <form @submit.prevent="register" class="space-y-6">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="firstName" class="block text-sm font-medium text-gray-300 mb-2">First Name</label>
              <input
                id="firstName"
                v-model="form.firstName"
                type="text"
                required
                class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                placeholder="John"
              >
            </div>
            <div>
              <label for="lastName" class="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
              <input
                id="lastName"
                v-model="form.lastName"
                type="text"
                required
                class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                placeholder="Doe"
              >
            </div>
          </div>
          
          <div>
            <label for="email" class="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
              placeholder="john@example.com"
            >
          </div>
          
          <div>
            <label for="restaurantName" class="block text-sm font-medium text-gray-300 mb-2">Restaurant Name</label>
            <input
              id="restaurantName"
              v-model="form.restaurantName"
              type="text"
              required
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
              placeholder="Bella Vista Restaurant"
            >
          </div>
          
          <div>
            <label for="password" class="block text-sm font-medium text-gray-300 mb-2">Password</label>
            <div class="relative">
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                placeholder="Create a strong password"
              >
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <EyeIcon v-if="!showPassword" class="w-5 h-5" />
                <EyeOffIcon v-else class="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
            <input
              id="confirmPassword"
              v-model="form.confirmPassword"
              type="password"
              required
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
              placeholder="Confirm your password"
            >
          </div>
          
          <div class="flex items-start">
            <input
              v-model="form.acceptTerms"
              type="checkbox"
              required
              class="w-4 h-4 text-primary-500 bg-dark-700 border-gray-600 rounded focus:ring-primary-500 focus:ring-2 mt-1"
            >
            <label class="ml-2 text-sm text-gray-300">
              I agree to the 
              <a href="#" class="text-primary-400 hover:text-primary-300">Terms of Service</a> 
              and 
              <a href="#" class="text-primary-400 hover:text-primary-300">Privacy Policy</a>
            </label>
          </div>
          
          <button
            type="submit"
            :disabled="loading || !isFormValid"
            class="w-full btn-primary"
            :class="{ 'opacity-50 cursor-not-allowed': loading || !isFormValid }"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating Account...
            </span>
            <span v-else>Create Account</span>
          </button>
        </form>
        
        <div class="mt-8 text-center">
          <p class="text-gray-300">
            Already have an account?
            <router-link to="/login" class="text-primary-400 hover:text-primary-300 font-medium">Sign in</router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { QrCodeIcon, ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-vue-next'
import { apiService } from '../services/api'
import { useToast } from '@/composables/useToast'

export default {
  name: 'Register',
  components: {
    QrCodeIcon,
    ArrowLeftIcon,
    EyeIcon,
    EyeOffIcon
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        restaurantName: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false
      },
      showPassword: false,
      loading: false
    }
  },
  computed: {
    isFormValid() {
      return this.form.firstName && 
             this.form.lastName && 
             this.form.email && 
             this.form.restaurantName && 
             this.form.password && 
             this.form.confirmPassword &&
             this.form.password === this.form.confirmPassword &&
             this.form.acceptTerms
    }
  },
  methods: {
    async register() {
      if (this.form.password !== this.form.confirmPassword) {
        this.toast.error('Password Mismatch', 'Passwords do not match. Please ensure both password fields are identical.')
        return
      }
      
      this.loading = true
      try {
        const response = await apiService.register({
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          restaurantName: this.form.restaurantName,
          password: this.form.password
        })
        
        // Store authentication data (C# backend returns PascalCase properties)
        localStorage.setItem('authToken', response.Token)
        localStorage.setItem('user', JSON.stringify(response.User))
        this.$router.push('/dashboard')
      } catch (error) {
        console.error('Registration error:', error)
        this.toast.error('Registration Failed', error.message || 'Unable to create account. Please try again.')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
